import React, {useEffect, useRef} from 'react';

import moment from 'moment-tz';
import {NavLink, PopoverBody} from 'reactstrap';

import {StyledModalHeaderDIV} from 'app/components/general/TabbedModal';
import {DashboardFilterbarSort} from 'app/general/dashboards/DashboardFilterbarSort';
import {
  DateRangeType,
  useDateRange,
  VALID_RANGE_TEMPLATE,
} from 'app/general/dates/date-range';
import {AttachOverlay, PopoverForm} from 'app/general/overlays';
import {SweetDatePicker} from 'app/general/widgets/SweetDatePicker';
import {DEFAULT_INTERVAL_FILTER} from 'app/tasks/interval-filter';
import {gt} from 'app/utils/i18n';

interface Props {
  dueSlug?: VALID_RANGE_TEMPLATE;
  dueStart?: string;
  dueEnd?: string;
  ordering?: string;
  applyFilter: (params: Record<string, string | number | null | void>) => void;
  userTimezone: string;
  filteringByTemplateId: boolean;
}

export function TasksFilterbarDueDate({
  dueStart,
  dueEnd,
  dueSlug,
  ordering = '',
  applyFilter,
  filteringByTemplateId,
  userTimezone,
}: Props): JSX.Element {
  const {
    rangeTemplates,
    selectRangeTemplate,
    customRange,
    selectCustomRange,
    getRangeTitle,
  } = useDateRange({
    initialValue: dueSlug == null ? 'custom' : dueSlug,
    templates: [
      'today',
      'all-time',
      'all-time-30-days',
      'this-week',
      'this-month',
      'next-week',
      'next-month',
      'next-7-days',
      'next-30-days',
    ],
  });
  const validRange = useRef<DateRangeType | null>(null);

  useEffect(() => {
    selectRangeTemplate(dueSlug || 'custom');
  }, [dueSlug]);

  useEffect(() => {
    selectCustomRange({
      start: dueStart || '',
      end: dueEnd || '',
    });
  }, [dueStart, dueEnd]);

  function setPredefinedValue(templateKey: VALID_RANGE_TEMPLATE) {
    applyFilter({
      interval: templateKey,
      start: '',
      end: '',
    });
  }

  function handleCalendarChange(dates: Array<Date>) {
    // user just picked start date, let's calculate valid range
    if (dates.length === 1) {
      setValidRange(dates[0]);
    } else if (dates.length === 2) {
      const start = moment
        .tz(dates[0], userTimezone)
        .startOf('day')
        .toISOString();
      const end = moment.tz(dates[1], userTimezone).endOf('day').toISOString();
      applyFilter({
        interval: '',
        start,
        end,
      });
    }
  }

  function handleSortChange(ordering: string) {
    applyFilter({
      ordering,
    });
  }

  function setValidRange(startDate: Date) {
    // since we will use 23h59m59s of the last day, it really means 93 days
    const DAYS_LIMIT_NUMBER = 92;
    validRange.current = {
      start: moment(startDate).toISOString(),
      end: moment(startDate).add(DAYS_LIMIT_NUMBER, 'days').toISOString(),
    };
  }

  const isValidDate = (date: Date) => {
    const momentDate = moment(date);
    return validRange.current != null
      ? momentDate.isBetween(validRange.current.start, validRange.current.end)
      : true;
  };

  function getTitle() {
    if (dueSlug != null || (dueStart != null && dueEnd != null)) {
      return getRangeTitle();
    }

    return filteringByTemplateId
      ? gt('No Date')
      : DEFAULT_INTERVAL_FILTER.label();
  }

  return (
    <AttachOverlay
      trigger="click focus"
      overlay={({overlayProps}) => (
        <PopoverForm overlayProps={{...overlayProps, placement: 'bottom'}}>
          {({close}) => (
            <>
              <StyledModalHeaderDIV>
                <ul className="nav nav-tabs">
                  <NavLink active={true}>
                    <i className="fa fa-calendar-alt mr-1" />
                    {gt('Filter by date')}
                  </NavLink>
                </ul>
                <button
                  onClick={close}
                  type="button"
                  className="close"
                  aria-label="Close"
                >
                  <i className="fa fa-times" aria-hidden="true"></i>
                </button>
              </StyledModalHeaderDIV>
              <PopoverBody>
                <div className="container-fluid">
                  {(() => {
                    let rows: Array<JSX.Element> = [];

                    for (let i = 0; i < rangeTemplates.length; i += 2)
                      rows.push(
                        <div className="row" key={i}>
                          {rangeTemplates.slice(i, i + 2).map((value) => (
                            <button
                              key={value.key}
                              className={`btn btn-light btn-sm d-block col-6 ${
                                dueSlug === value.key ? 'active' : ''
                              }`}
                              onClick={() => setPredefinedValue(value.key)}
                            >
                              {value.label()}
                            </button>
                          ))}
                        </div>
                      );

                    return rows;
                  })()}
                  <div className="row mt-2">
                    <label>{gt('Custom date range')}</label>
                    <SweetDatePicker
                      onClose={() => (validRange.current = null)}
                      onChange={(dates) => handleCalendarChange(dates)}
                      value={[customRange.start, customRange.end]}
                      className="input-group input-group-sm"
                      options={{
                        mode: 'range',
                        enable: [isValidDate],
                      }}
                    >
                      <div className="input-group-prepend">
                        <div className="input-group-text">
                          <i className="fa fa-calendar-alt" />
                        </div>
                      </div>
                      <input
                        data-input={true}
                        readOnly={true}
                        placeholder={gt('Click to select...')}
                        className="form-control"
                        type="text"
                      />
                    </SweetDatePicker>
                  </div>
                </div>
                <label className="mt-2">{gt('Sort results by')}</label>
                <DashboardFilterbarSort
                  options={['due', '-due']}
                  value={ordering}
                  onChange={(value) => handleSortChange(value)}
                />
              </PopoverBody>
            </>
          )}
        </PopoverForm>
      )}
    >
      {(triggers) => (
        <div
          className="form-control d-flex align-items-center justify-content-between"
          {...triggers}
        >
          {getTitle()} <i className="fa fa-chevron-down" />
        </div>
      )}
    </AttachOverlay>
  );
}
